import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import ChevronIcon from '~/assets/icons/chevron.svg';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';
import {
  Item,
  ItemDefinition,
  ItemValidationError,
  registry,
  RenderProps
} from '../Item';
import { ACCORDION, ACCORDION_ITEM } from '../itemIds';
import { useAccordionStore } from './AccordionStore';
import { accordionLearnBlockerId } from './accordionBlockerIds';
import { useEditorStore, SequenceItem } from '~/shell/Store';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import LightButton from '~/editor/buttons/LightButton';
import { TrashIconForBtn } from '~/training/sequence/templates/items';
import { handlePaste } from '../helper/eventForContentEditable';

const Chevron = animated(
  styled.div({
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '> svg': {
      stroke: 'black'
    }
  })
);

const DraggableElement = styled.div<{ moveCursor?: boolean }>`
  cursor: ${props => (props.moveCursor ? 'move !important' : 'default')};
  &.drag-over-top {
    border-top: 2px solid #9845fb !important;
    border-bottom: 2px solid transparent !important;
    padding-top: 10px;
  }

  &.drag-over-bottom {
    border-top: 2px solid transparent !important;
    border-bottom: 2px solid #9845fb !important;
    padding-bottom: 10px;
  }
`;

const DragAndDropIconForBtn = styled(DragAndDropIcon)`
  cursor: move !important;
  z-index: 100;
  margin-top: 8px;
  width: 20px;
  height: 20px;
`;

const StyledSpan = styled.span`
  flex: 1 1 0%;

  b {
    font-family: recoletaBold !important;
  }

  &[contenteditable='true'] {
    position: relative;
    border: 2px solid transparent;

    &:focus:after {
      opacity: 0;
    }

    &:hover {
      border: 2px solid #9738ff;
    }
  }
`;

interface Props {
  value: string;
  expandAllItems?: boolean;
}

export type AccordionItemDefinition = ItemDefinition<
  typeof ACCORDION_ITEM,
  Props
>;

const ContentBox = styled.div`
  padding: 10px 10px 10px 20px;
  border-radius: 20px;
  background-color: #efefef;
  z-index: 1;
`;

export class AccordionItem extends Item<typeof ACCORDION_ITEM, Props> {
  protected validate() {
    if (!this.parent || this.parent.type !== ACCORDION) {
      throw new ItemValidationError(
        `An item of type [${ACCORDION_ITEM}] can only be a child of an item of type [${ACCORDION}]`
      );
    }

    if (!this.definition.items?.length) {
      throw new ItemValidationError(
        `An item of type [${ACCORDION_ITEM}] must have at least one child item`
      );
    }
  }

  protected render({
    id,
    value,
    children,
    expandAllItems = true,
    parent
  }: RenderProps<Props>) {
    const { open, openItem } = useAccordionStore();
    const {
      editorMode,
      contentEditable,
      removeSubItem,
      updateSequence,
      sequence,
      apiService
    } = useEditorStore();
    const { addBlocker, removeBlocker } = useSequenceStore();

    useEffect(() => {
      if (editorMode || !expandAllItems) return;

      addBlocker(`accordion_item_${id}`);

      return () => removeBlocker(`accordion_item_${id}`);
    }, [editorMode, expandAllItems, id, addBlocker, removeBlocker]);

    const [classNameForDragOver, setClassNameForDragOver] =
      useState<string>('');
    const [isDragOver, setIsDragOver] = useState(false);

    const expanded = openItem === id;

    const props = useSpring({
      to: { transform: !expanded ? 'rotate(90deg)' : 'rotate(-90deg)' },
      from: { transform: 'rotate(90deg)' }
    });

    const handleDelete = () => {
      removeSubItem(id);
    };

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      let newText = event.target.innerHTML;
      newText = newText.replace(/<div\s*><br\s*><\/div\s*>/gi, '<br>');

      let cleanText = newText.replace(/<div><br><\/div>/g, '<br>');
      cleanText = cleanText.replace(/<div>/g, '<br>');
      cleanText = cleanText.replace(/<\/div><br>/g, '<br>');
      cleanText = cleanText.replace(/<div>/g, '\n').replace(/<\/div>/g, '');

      if (!newText.startsWith('<div>')) {
        const firstDivIndex = cleanText.indexOf('\n');
        if (firstDivIndex !== -1) {
          cleanText =
            cleanText.slice(0, firstDivIndex) +
            '\n' +
            cleanText.slice(firstDivIndex + 1);
        }
      }

      if (cleanText.trim() == '') {
        cleanText = 'Type Here';
        event.target.innerHTML = cleanText;
      }

      updateSequence(id, 'title', cleanText);
    };

    const getCountOfItems = () =>
      sequence?.items?.filter((item: SequenceItem) => item.type == 'LIST')[0]
        ?.items?.length;

    const handleInput = (event: React.ChangeEvent<HTMLDivElement>) => {
      let newText = event.currentTarget.textContent;

      const textWithoutHtml = newText?.replace(/<[^>]*>/g, '');

      if (textWithoutHtml?.trim() == '') {
        newText = '';
        event.currentTarget.textContent = newText;

        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(event.currentTarget);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      if (
        contentEditable &&
        event.currentTarget.textContent?.toLowerCase() === 'type here'
      ) {
        const element = event.currentTarget;
        element.textContent = '';
        element.focus();

        setTimeout(() => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(element);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }, 0);
      }
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      if (!contentEditable) {
        return;
      }

      e.dataTransfer.setData('text/plain', id);

      if (window) {
        window.sessionStorage.setItem('dragItemId', id);
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
      if (!contentEditable) {
        return;
      }

      e.preventDefault();
      setIsDragOver(true);

      if (window) {
        const dragItemId = window.sessionStorage.getItem('dragItemId');
        const currentId = (e.currentTarget.closest('[data-id]') as HTMLElement)
          ?.dataset.id;

        if (dragItemId === currentId) {
          setIsDragOver(false);
          return;
        }

        const textItem = sequence!.items.find(
          (item: SequenceItem) => item.type === 'LIST'
        );

        if (!textItem || !textItem.items) return;

        const dragIndex = textItem.items.findIndex(
          (item: { id: string }) => item.id == dragItemId
        );
        const dropIndex = textItem.items.findIndex(
          (item: { id: string }) => item.id == currentId
        );

        if (dragIndex === dropIndex) {
          setClassNameForDragOver('');
        } else {
          setClassNameForDragOver(
            dropIndex > dragIndex ? 'drag-over-bottom' : 'drag-over-top'
          );
        }
      }
    };

    const handleDrop = (e: React.DragEvent<HTMLElement>) => {
      if (!contentEditable || !sequence) {
        return;
      }

      e.preventDefault();
      setIsDragOver(false);

      const dragItemId = e.dataTransfer.getData('text/plain');

      if (!sequence) return;

      const textItem = sequence.items.find(
        (item: SequenceItem) => item.type === 'LIST'
      );
      if (!textItem || !textItem.items) return;

      const currentId = (e.currentTarget.closest('[data-id]') as HTMLElement)
        ?.dataset.id;
      if (!currentId) return;

      const dragIndex = textItem.items.findIndex(
        (item: { id: string }) => item.id == dragItemId
      );
      const dropIndex = textItem.items.findIndex(
        (item: { id: string }) => item.id == currentId
      );

      if (
        dragIndex === -1 ||
        dropIndex === -1 ||
        (dragItemId as unknown) === dropIndex
      )
        return;

      const dragElement = document.querySelector(`[data-id="${dragItemId}"]`);
      const dropElement = document.querySelector(`[data-id="${currentId}"]`);

      if (!dragElement || !dropElement) return;

      const parent = dragElement.parentElement;
      if (!parent) return;

      if (dropIndex > dragIndex) {
        try {
          parent.insertBefore(dragElement, dropElement.nextSibling);
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      } else {
        try {
          parent.insertBefore(dragElement, dropElement);
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      }

      const newItems = [...textItem.items];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);

      const updatedSequence = {
        ...sequence,
        items: sequence.items.map((item: SequenceItem) =>
          item.type === 'LIST' ? { ...item, items: newItems } : item
        )
      };

      newItems.forEach((item, index) => {
        if (item.id == dragItemId) {
          apiService?.reorderSubItem(item.id, index + 1);
        }
      });

      Object.assign(sequence, updatedSequence);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsDragOver(false);
    };

    const isDraggable = contentEditable && (getCountOfItems() as number) > 1;

    return (
      <DraggableElement
        draggable={isDraggable}
        data-id={id}
        data-testid="accordion-item"
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
        className={isDragOver ? classNameForDragOver : ''}
        moveCursor={contentEditable}
      >
        <x.button
          border="none"
          bg={expanded ? 'accentDark' : 'transparent'}
          color={expanded ? 'white' : 'black'}
          p={'sm md'}
          // m={expanded ? '0 -md' : '0'}
          aria-expanded={expanded}
          textAlign="left"
          display="flex"
          borderRadius="md"
          style={{ zIndex: 1, width: '100%', cursor: 'move !important' }}
          onClick={() => {
            open(id);
            if (!editorMode) {
              if (expandAllItems) {
                removeBlocker(`accordion_item_${id}`);
              } else if (parent?.id) {
                removeBlocker(accordionLearnBlockerId(parent.id));
              }
            }
          }}
          className="itemStyleForDarkTheme"
          data-testid="accordion-item-trigger"
        >
          {contentEditable && (
            <div
              data-testid="accordion-item-drag-handle"
              style={{ cursor: 'move', display: 'flex', alignItems: 'center' }}
            >
              <DragAndDropIconForBtn stroke={expanded ? 'white' : 'default'} />
            </div>
          )}
          <StyledSpan
            style={{ flex: 1 }}
            contentEditable={contentEditable}
            onBlur={contentEditable ? handleBlur : undefined}
            onInput={contentEditable ? handleInput : undefined}
            onPaste={contentEditable ? handlePaste : undefined}
            onFocus={contentEditable ? handleFocus : undefined}
            suppressContentEditableWarning={true}
            dangerouslySetInnerHTML={{ __html: value }}
            data-id={id}
            data-testid="accordion-item-title"
          ></StyledSpan>
          <Chevron style={props}>
            <ChevronIcon />
          </Chevron>
        </x.button>
        <ContentBox
          className="_AccordionContentBox"
          style={{ display: !expanded ? 'none' : 'block', margin: '10px' }}
          data-id={id}
          data-testid="accordion-item-content"
        >
          {children}
        </ContentBox>
        {contentEditable && (getCountOfItems() as number) > 1 && (
          <LightButton
            data-testid="accordion-item-delete-button"
            title="Remove"
            onClick={handleDelete}
            style={{
              padding: '11px',
              width: '38px',
              height: '38px',
              display: 'block',
              marginLeft: 'auto',
              marginTop: '5px',
              zIndex: 2,
              marginRight: '10px'
            }}
          >
            <TrashIconForBtn
              style={{ marginLeft: '-5px', marginTop: '-5px' }}
            />
          </LightButton>
        )}
      </DraggableElement>
    );
  }
}

registry[ACCORDION_ITEM] = AccordionItem;
